/** One dispatched Server-Sent Events frame. */
export interface SseEvent {
  /** The `event:` field, or 'message' when the frame has none. */
  event: string;
  /** Every `data:` line of the frame, joined with '\n'. */
  data: string;
}

/**
 * Incremental `text/event-stream` parser, for reading SSE through fetch + ReadableStream
 * (EventSource cannot send the session Bearer in a header).
 *
 * Feed it decoded text in whatever chunks the network delivers; it returns the frames that
 * completed in that chunk. Follows the WHATWG event-stream rules that matter here:
 * - lines end in \n, \r\n or \r, and a chunk may split a line (or a \r\n) anywhere;
 * - a blank line dispatches the frame; a frame without data is not dispatched;
 * - lines starting with ':' are comments (the server's `: ping` keep-alive) and are ignored;
 * - one space after the colon is stripped; `id:` and `retry:` are ignored.
 */
export class SseFrameParser {

  private buffer = '';
  private eventName = '';
  private dataLines: string[] = [];
  /** The previous chunk ended in \r, so a leading \n in the next one belongs to it. */
  private pendingCr = false;

  push(chunk: string): SseEvent[] {
    const events: SseEvent[] = [];
    let text = chunk;
    // An empty chunk (TextDecoder can return one mid-character) says nothing about the \n.
    if (this.pendingCr && text.length > 0) {
      if (text.startsWith('\n')) {
        text = text.slice(1);
      }
      this.pendingCr = false;
    }
    this.buffer += text;

    let start = 0;
    for (let i = 0; i < this.buffer.length; i++) {
      const ch = this.buffer[i];
      if (ch !== '\n' && ch !== '\r') {
        continue;
      }
      const line = this.buffer.slice(start, i);
      if (ch === '\r') {
        if (i + 1 < this.buffer.length) {
          if (this.buffer[i + 1] === '\n') {
            i++;
          }
        }
        else {
          this.pendingCr = true;
        }
      }
      start = i + 1;
      const event = this.processLine(line);
      if (event) {
        events.push(event);
      }
    }
    this.buffer = this.buffer.slice(start);
    return events;
  }

  private processLine(line: string): SseEvent | null {
    if (line === '') {
      return this.dispatch();
    }
    if (line.startsWith(':')) {
      return null;
    }
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    let value = colon === -1 ? '' : line.slice(colon + 1);
    if (value.startsWith(' ')) {
      value = value.slice(1);
    }
    if (field === 'event') {
      this.eventName = value;
    }
    else if (field === 'data') {
      this.dataLines.push(value);
    }
    return null;
  }

  private dispatch(): SseEvent | null {
    const hasData = this.dataLines.length > 0;
    const event: SseEvent = { event: this.eventName || 'message', data: this.dataLines.join('\n') };
    this.eventName = '';
    this.dataLines = [];
    return hasData ? event : null;
  }
}
